
import { useEffect, useId, useMemo, useState } from "react";
import "./Modal.css";
import { Input, Modal } from "@iq/models";
import { createContact, updateContact } from "../../services/contact_service";
import { getLeadForgeAssessment } from "../../services/account_service";
import useLookupStore from "../../states/useLookupStore";
import { FiSave, FiUser } from "react-icons/fi";

function buildInitialForm(initialContact) {
    return {
        email: initialContact?.email || "",
        firstName: initialContact?.firstName || initialContact?.first_name || "",
        lastName: initialContact?.lastName || initialContact?.last_name || "",
        account: initialContact?.account || initialContact?.company || "",
        accountId: initialContact?.accountId || initialContact?.account_id || null,
        company: initialContact?.company || initialContact?.account || "",
        jobTitle: initialContact?.jobTitle || initialContact?.job_title || initialContact?.title || "",
        phone: initialContact?.phone || "",
        mobile: initialContact?.mobile || initialContact?.phone || "",
        primaryProblem: initialContact?.primaryProblem || initialContact?.primary_problem || "",
    };
}

function NewContact({
    onClose,
    mode = "create",
    contactId = null,
    initialContact = null,
    onCreated,
    onUpdated,
}) {
    const contacts = useLookupStore((state) => state.contacts);
    const accountOptions = useLookupStore((state) => state.accounts);
    const accountsError = useLookupStore((state) => state.accountsError);
    const hydrateAccounts = useLookupStore((state) => state.hydrateAccounts);
    const setLookups = useLookupStore((state) => state.setLookups);
    const formId = useId();
    const isEditMode = mode === "edit";

    const [form, setForm] = useState(() => buildInitialForm(initialContact));
    const [accountQuery, setAccountQuery] = useState(() => buildInitialForm(initialContact).account);
    const [submitting, setSubmitting] = useState(false);
    const [assessmentLoading, setAssessmentLoading] = useState(false);
    const [submitError, setSubmitError] = useState("");
    useEffect(() => {
        hydrateAccounts();
    }, [hydrateAccounts]);

    useEffect(() => {
        const nextForm = buildInitialForm(initialContact);
        if (isEditMode) {
            setForm(nextForm);
            setAccountQuery(nextForm.account);
        }
    }, [initialContact, isEditMode]);

    useEffect(() => {
        const initialAccountId = initialContact?.accountId || initialContact?.account_id;
        if (isEditMode || !initialAccountId || initialContact?.primaryProblem || initialContact?.primary_problem) {
            return;
        }

        let cancelled = false;
        setAssessmentLoading(true);
        getLeadForgeAssessment(initialAccountId)
            .then((assessment) => {
                if (!cancelled) {
                    setForm((prev) => ({ ...prev, primaryProblem: assessment?.primary_problem || "" }));
                }
            })
            .catch((err) => {
                if (!cancelled) setSubmitError(err.message || "Failed to load account sales context");
            })
            .finally(() => {
                if (!cancelled) setAssessmentLoading(false);
            });

        return () => { cancelled = true; };
    }, [initialContact, isEditMode]);

    function handleChange(e) {
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    function handleAccountInputChange(e) {
        const value = e.target.value;
        setAccountQuery(value);
        setForm((prev) => ({ ...prev, account: value, accountId: null }));
    }

    async function selectExistingAccount(account) {
        setAccountQuery(account.name);
        setForm((prev) => ({ ...prev, account: account.name, accountId: account.id || null, primaryProblem: "" }));
        if (!account.id || isEditMode) {
            return;
        }

        setAssessmentLoading(true);
        setSubmitError("");
        try {
            const assessment = await getLeadForgeAssessment(account.id);
            setForm((prev) => (
                Number(prev.accountId) === Number(account.id)
                    ? { ...prev, primaryProblem: assessment?.primary_problem || "" }
                    : prev
            ));
        } catch (err) {
            setSubmitError(err.message || "Failed to load account sales context");
        } finally {
            setAssessmentLoading(false);
        }
    }

    function createNewAccountFromQuery() {
        const value = accountQuery.trim();
        if (!value) {
            return;
        }
        setForm((prev) => ({ ...prev, account: value, accountId: null }));
        setAccountQuery(value);
    }

    const normalizedQuery = accountQuery.trim().toLowerCase();
    const filteredAccounts = useMemo(() => {
        return accountOptions.filter((account) => account.name.toLowerCase().includes(normalizedQuery));
    }, [accountOptions, normalizedQuery]);

    const canCreateNewAccount = Boolean(normalizedQuery) &&
        !accountOptions.some((account) => account.name.toLowerCase() === normalizedQuery);

    const hasFirstName = Boolean(form.firstName?.trim());
    const hasLastName = Boolean(form.lastName?.trim());

    const actionButtons = useMemo(() => ([
        {
            key: "save",
            text: submitting
                ? (isEditMode ? "Saving changes..." : "Saving...")
                : (isEditMode ? "Save changes" : "Save contact"),
            icon: <FiSave />,
            type: "submit",
            form: formId,
            disabled: submitting,
            color: "accent",
        },
    ]), [formId, isEditMode, submitting]);

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitError("");
        
        // Validate required fields
        const hasName = form.firstName?.trim() || form.lastName?.trim();
        if (!hasName || !form.account || !form.mobile || (!isEditMode && !form.primaryProblem?.trim())) {
            setSubmitError("Please fill in all required fields: first or last name, account, primary problem, and mobile number");
            return;
        }
        
        setSubmitting(true);
        try {
            const trimmedAccount = form.account.trim();
            const payload = {
                ...form,
                account: trimmedAccount,
                company: form.company || trimmedAccount,
            };

            let saved;
            if (isEditMode) {
                const resolvedContactId = Number(contactId || initialContact?.id || 0);
                if (!resolvedContactId) {
                    throw new Error("Missing contact id for edit");
                }
                saved = await updateContact(resolvedContactId, payload);
                onUpdated?.(saved);
            } else {
                saved = await createContact(payload);
                onCreated?.(saved);
            }

            if (!isEditMode) {
                const fullName = `${form.firstName} ${form.lastName}`.trim();
                const createdContact = {
                    id: saved?.id,
                    account_id: saved?.account_id,
                    name: fullName,
                    email: form.email,
                    account: trimmedAccount,
                };

                const savedAccountId = saved?.account_id;
                const nextAccounts = savedAccountId && !accountOptions.some((account) => Number(account.id) === Number(savedAccountId))
                    ? [...accountOptions, { id: savedAccountId, name: trimmedAccount }]
                    : accountOptions;
                setLookups({ contacts: [createdContact, ...contacts], accounts: nextAccounts });
            } else {
                const nextContacts = contacts.map((contact) => Number(contact.id) === Number(saved?.id) ? saved : contact);
                setLookups({ contacts: nextContacts });
            }

            // Success! Close the modal (parent will handle refresh)
            onClose?.();
        } catch (err) {
            setSubmitError(err.message || "Failed to save contact");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={isEditMode ? "Edit Contact" : "New Contact"}
            icon={<FiUser />}
            actionButtons={actionButtons}
            errorMessage={submitError || accountsError}
            shrink={true}
        >
                <form id={formId} className="modal-form-container" onSubmit={handleSubmit}>
                    <div className="form-fields-container">
                        <div className="modal-form-field wide-form-field">
                            <label htmlFor="email">Email <span className="modal-form-field-note">(optional)</span></label>
                            <Input id="email" name="email" type="email" placeholder="E.g. john.smith@acmecorp.com" value={form.email} onChange={handleChange} colorScheme="light" />
                        </div>
                        <div className="modal-form-field">
                            <label htmlFor="firstName">First name {hasLastName && <span className="modal-form-field-note">(optional)</span>}</label>
                            <Input id="firstName" name="firstName" type="text" placeholder="E.g. John" value={form.firstName} onChange={handleChange} colorScheme="light" />
                        </div>

                        <div className="modal-form-field">
                            <label htmlFor="lastName">Last name {hasFirstName && <span className="modal-form-field-note">(optional)</span>}</label>
                            <Input id="lastName" name="lastName" type="text" placeholder="E.g. Smith" value={form.lastName} onChange={handleChange} colorScheme="light" />
                        </div>
                        <div className="modal-form-field">
                            <label htmlFor="account">Account <span className="modal-form-field-note" style={{ color: "var(--color-danger)" }}>*</span></label>
                            <Input
                                type="dropdown"
                                id="account"
                                name="account"
                                placeholder="E.g. Acme Inc."
                                value={accountQuery}
                                onChange={handleAccountInputChange}
                                allowCustom={true}
                                colorScheme="light"
                                ariaLabel="Account suggestions"
                                renderInPortal={true}
                            >
                                {({ closeMenu }) => (
                                    <>
                                        {filteredAccounts.map((account) => (
                                            <button
                                                key={account.id || account.name}
                                                type="button"
                                                className="dropdown-menu-item"
                                                onClick={() => {
                                                    selectExistingAccount(account);
                                                    closeMenu();
                                                }}
                                            >
                                                {account.name}
                                            </button>
                                        ))}

                                        {canCreateNewAccount && (
                                            <button
                                                type="button"
                                                className="dropdown-menu-item dropdown-menu-item-create"
                                                onClick={() => {
                                                    createNewAccountFromQuery();
                                                    closeMenu();
                                                }}
                                            >
                                                Create new account: "{accountQuery.trim()}"
                                            </button>
                                        )}

                                        {!filteredAccounts.length && !canCreateNewAccount && (
                                            <div className="dropdown-menu-empty">No accounts found</div>
                                        )}
                                    </>
                                )}
                            </Input>
                        </div>
                        
                        {/* Dummy form field for spacing */}
                        <div className="modal-form-field"></div>

                        <div className="modal-form-field">
                            <label htmlFor="jobTitle">Job title</label>
                            <Input id="jobTitle" name="jobTitle" type="text" placeholder="E.g. Co-founder" value={form.jobTitle} onChange={handleChange} colorScheme="light" />
                        </div>
                        <div className="modal-form-field">
                            <label htmlFor="phone">Phone</label>
                            <Input id="phone" name="phone" type="tel" placeholder="E.g. +17145965875" value={form.phone} onChange={handleChange} colorScheme="light" />
                        </div>
                        <div className="modal-form-field">
                            <label htmlFor="mobile">Mobile <span style={{ color: "var(--color-danger)" }}>*</span></label>
                            <Input id="mobile" name="mobile" type="tel" placeholder="E.g. +17145965875" value={form.mobile} onChange={handleChange} colorScheme="light" />
                        </div>

                        {!isEditMode ? (
                            <div className="modal-form-field wide-form-field">
                                <label htmlFor="primaryProblem">Primary problem <span style={{ color: "var(--color-danger)" }}>*</span></label>
                                <Input
                                    id="primaryProblem"
                                    name="primaryProblem"
                                    type="textarea"
                                    rows={3}
                                    placeholder="What business problem gives the salesperson a reason to contact this account?"
                                    value={form.primaryProblem}
                                    onChange={handleChange}
                                    colorScheme="light"
                                    disabled={assessmentLoading}
                                />
                                {assessmentLoading ? <span className="modal-form-field-note">Loading account sales context...</span> : null}
                            </div>
                        ) : null}

                    </div>
                </form>
        </Modal>
    );
}

export default NewContact;

